<template>
  <div class="primaryFont flex flex-row gap-4">
    <div class="leftPanel">
      <h3 :style="{ color: 'black', fontSize: '24px' }">Vinculados Guardados</h3>
      <div class="flex flex-column gap-3">
        <template v-if="vinculatedStore.vinculatedList.length">
          <CustomCardTab
            v-for="vinculated in vinculatedStore.vinculatedList"
            :statusCalabozo="statusCalabozoFor(vinculated.ActuacionPersonaFisicaID)"
            :key="vinculated.ActuacionPersonaFisicaID"
            :status="vinculated.TipoRolPersonaFisicaID?.nombre || vinculated.TipoRolPersonaFisicaID"
            :isSelected="vinculatedStore.isSelected(vinculated.ActuacionPersonaFisicaID)"
            :disabled="
              vinculatedStore.isCreate || vinculatedStore.isLoading || vinculatedStore.isEdit
            "
            @click="() => handleSelect(vinculated)"
          >
            <template #title>
              <span class="cardTitle">
                {{
                  vinculated.Nombre
                    ? `${vinculated.Apellido}, ${vinculated.Nombre}`
                    : vinculated.Apellido
                }}
              </span>
            </template>

            <template #content>
              <span class="cardText">
                {{ vinculated.TipoRolPersonaFisicaID }}
              </span>
            </template>
          </CustomCardTab>
        </template>
        <!-- Card simbólica para modo create -->
        <CustomCardTab
          v-if="vinculatedStore.isCreate && !vinculatedStore.isLoading"
          :key="'create-preview'"
          :status="vinculatedStore.form.TipoRolPersonaFisicaID?.nombre || 'Sin estado'"
          :isSelected="true"
          :isPreview="true"
          @click="() => {}"
        >
          <template #title>
            <div>
              {{
                vinculatedStore.form.nombre
                  ? vinculatedStore.form.apellido + ", " + vinculatedStore.form.nombre
                  : vinculatedStore.form.apellido || "Sin nombre"
              }}
            </div>
          </template>

          <template #content>
            <div>
              {{ vinculatedStore.form.TipoRolPersonaFisicaID?.nombre || "Sin estado" }}
            </div>
          </template>
        </CustomCardTab>
        <Button
          class="plusButton mt-2"
          label="Nuevo Vinculado"
          icon="pi pi-plus"
          iconPos="left"
          @click="handleCreate"
          :disabled="
            vinculatedStore.isCreate || vinculatedStore.isLoading || vinculatedStore.isEdit
          "
        />
      </div>
    </div>
    <!-- Right Panel (Inputs) -->
    <div class="ml-3 rightPanel secondaryFont">
      <div
        v-if="
          vinculatedStore.vinculatedList.length &&
          !vinculatedStore.selectedId &&
          !vinculatedStore.isCreate
        "
      >
        <p class="informativeText secondaryFont">Seleccione un vinculado</p>
      </div>

      <div
        v-if="
          selectedPersona &&
          selectedPersona.Apellido === 'Persona de Filiacion desconocida' &&
          !vinculatedStore.isEdit
        "
      >
        <p class="informativeText secondaryFont text-2xl">
          <b>Persona de Filiación Desconocida</b>
        </p>
      </div>

      <div v-if="!vinculatedStore.vinculatedList.length && !vinculatedStore.isCreate">
        <p class="informativeText secondaryFont">
          No hay vinculados en esta actuación. Cree un nuevo vinculado
        </p>
      </div>

      <div v-else>
        <FormPersonaV2
          personType="vinculated"
          :personData="selectedPersona"
          :isDisable="isDisable"
          :key="selectedPersona?.ActuacionPersonaFisicaID || 'new'"
          @updatePerson="handleUpdatePerson"
          @validationStatus="handlePersonValidationStatus"
          :isCreate="vinculatedStore.isCreate"
          ref="formPersonaV2"
        />
        <IdentikitForm
          :key="identikitKey"
          ref="identikitRef"
          :arrId="selectedPersona?.ActuacionPersonaFisicaID"
          @updateValue="(field, value) => handleUpdateIdentikitValue(field, value)"
          @validationStatus="handleValidationStatus"
          :isCreate="vinculatedStore.isCreate || vinculatedStore.isEdit"
          :viewTabMode="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import CustomCardTab from "@/components/CustomCardTab.vue";
import { ref, computed, onMounted, watch } from "vue";
import { useStoreCompat, useRouterCompat } from "@/composables";
import { useCalabozoStore } from "@/store/Pinia/useCalabozoStore";

import { useVinculatedStore } from "@/store/Pinia/useVinculatedStore";
import FormPersonaV2 from "@/components/forms/FormPersonaV2.vue";
import IdentikitForm from "@/components/forms/IdentikitForm.vue";
import { storeToRefs } from "pinia";

const calabozoStore = useCalabozoStore();
const { detencionPorActuacionPersonaFisicaId } = storeToRefs(calabozoStore);

const statusCalabozoFor = (id) => {
  const det = detencionPorActuacionPersonaFisicaId.value[id];
  if (det === undefined) return ""; // todavía no llegó => no chip
  if (det === null) return ""; // pedido y vacío
  const raw = det.EstadoActual ?? det.estado ?? det.Estado;
  return calabozoStore.calabozoLabelFromApi
    ? calabozoStore.calabozoLabelFromApi(raw) // si la exportás
    : ({ EN_CALABOZO: "En Calabozo", PENDIENTE: "Pendiente", LIBERADO: "Liberado" }[
        (raw ?? "").toString().trim().toUpperCase()
      ] ?? "");
};
const props = defineProps({
  blockNavigationToAnotherTab: {
    type: Function,
    default: () => {},
  },
  removeTheBlockNavigationToAnotherTab: {
    type: Function,
    default: () => {},
  },
});

const identikitRef = ref(null);
const identikitKey = ref(0);

const store = useStoreCompat();
const { route } = useRouterCompat();
const vinculatedStore = useVinculatedStore();
const selectedPersona = computed({
  get: () => vinculatedStore.selected,
  set: (val) => {
    vinculatedStore.selected = val;
  },
});
const formPersonaV2 = ref(null);
defineExpose({
  formPersonaV2,
  selectedPersona,
});

const isDisabledRef = ref(false);

// Computed que maneja el estado de deshabilitación de los campos
const isDisable = computed({
  get: () => {
    // Si estamos creando un nuevo vinculado, los campos deben estar habilitados
    if (vinculatedStore.isCreate) return false;

    // En cualquier otro caso, se deshabilita si no está en modo edición
    return isDisabledRef.value || !vinculatedStore.isEdit;
  },
  set: (value) => {
    isDisabledRef.value = value;
  },
});

const tiposDoc = computed(() => store.state.siis.tipoDocumento);
const sexos = computed(() => store.state.siis.sexo);
const exhibeDoc = computed(() => store.state.siis.exhibeDocumento);
const nacionalidades = computed(() => store.state.siis.nacionalidad);
const estadosCiviles = computed(() => store.state.siis.estadoCivil);
const tieneEstudiosOptions = computed(() => store.state.siis.tieneEstudios);
const instruccionOptions = computed(() => store.state.siis.instruccion);
const previewData = computed(() => getCreatePreviewVinculated());

const handleSelect = (vinculated) => {
  if (!vinculatedStore.isCreate) {
    vinculatedStore.select(vinculated);
    selectedPersona.value = vinculated;
    // Al seleccionar un vinculado existente, aseguramos que no esté en modo edición
    if (vinculatedStore.isEdit) {
      vinculatedStore.isEdit = false;
    }
  }
  isCreate.value = false;
};
const handleCreate = () => {
  if (route.matched[0].path != "/newActuacion") {
    props.blockNavigationToAnotherTab();
  }
  vinculatedStore.startCreate();

  identikitKey.value++;

  selectedPersona.value = vinculatedStore.form;
  isCreate.value = true;

  setTimeout(() => {
    formPersonaV2.value.sipeMode = true;
  }, 100);
};

watch(
  () => vinculatedStore.isEdit,
  (newValue) => {
    isDisable.value = !newValue;
  }
);
watch(
  () => vinculatedStore.vinculatedList,
  async (list) => {
    if (!list?.length) return;

    const detenidos = list.filter(
      (v) => (v.TipoRolPersonaFisicaID?.nombre ?? v.TipoRolPersonaFisicaID) === "Detenido"
    );

    await Promise.all(
      detenidos.map((v) =>
        calabozoStore.fetchDetencionPorActuacionPersonaFisicaId(v.ActuacionPersonaFisicaID)
      )
    );
  },
  { immediate: true }
);

onMounted(() => {
  selectedPersona.value = null;
  if (!vinculatedStore.isCreate && vinculatedStore.vinculatedList.length > 0) {
    selectedPersona.value = vinculatedStore.vinculatedList[0];
  }
});

watch(
  selectedPersona,
  (newValue) => {
    if (identikitRef?.value) {
      identikitRef.value.listAnswers =
        identikitRef.value.listAnswers && identikitRef.value.listAnswers.hasOwnProperty("value")
          ? (() => {
              identikitRef.value.listAnswers.value = newValue?.Identikit || [];
              return identikitRef.value.listAnswers;
            })()
          : newValue?.Identikit || [];
    }
  },
  { deep: true },
  { immediate: true }
);

const getCreatePreviewVinculated = () => {
  const tipoDoc = tiposDoc.value.find((m) => m.TipoDoc === vinculatedStore.form.tipoDoc);
  const sexo = sexos.value.find((m) => m.ModeloID === vinculatedStore.form.sexo);
  const exhibeDocumento = exhibeDoc.value.find(
    (c) => c.CategoriaID === vinculatedStore.form.exhibeDocumento
  );
  const nacionalidad = nacionalidades.value.find(
    (t) => t.tipoID === vinculatedStore.form.nacionalidad
  );
  const estadoCivil = estadosCiviles.value.find(
    (t) => t.tipoID === vinculatedStore.form.estadoCivil
  );
  const tieneEstudios = tieneEstudiosOptions.value.find(
    (t) => t.tipoID === vinculatedStore.form.tieneEstudios
  );
  const instruccion = instruccionOptions.value.find(
    (t) => t.tipoID === vinculatedStore.form.instruccion
  );

  return {
    tipoDoc: tipoDoc?.nombre || "Sin tipo",
    sexo: sexo?.Nombre || "Sin sexo",
    exhibeDocumento: exhibeDocumento?.nombre || "Sin especificar",
    nacionalidad: nacionalidad?.Nombre || "Sin nacionalidad",
    estadoCivil: estadoCivil?.Nombre || "Sin estado civil",
    tieneEstudios: tieneEstudios?.nombre || "Sin especificar",
    instruccion: instruccion?.nombre || "Sin especificar",
  };
};

const vinculateds = computed(() => vinculatedStore.vinculatedList);

// Datos para Identikit
// IMPORTANTE: Usa ActuacionPersonaFisicaID en lugar de arrId
const arrId = computed(() => {
  const primerAcusado = store.state.sumario.acusados.list[0];
  return primerAcusado?.ActuacionPersonaFisicaID || null;
});
const identikitAnswer = ref([]);
const validationErrors = ref({});
const isCreate = ref(false);

const handleUpdateIdentikitValue = async (field, value) => {
  if (field === "listAnswers") {
    identikitAnswer.value = [...value];
  }
  const identikitWithActuacionPersonaFisicaID = identikitAnswer.value.map(
    ({ answerToTextAreaContent, ...answer }) => ({
      ...answer,
      questionNumber: answer.questionNumber.toString(),
      DescripcionPersonaFisicaID: selectedPersona?.value?.ActuacionPersonaFisicaID,
    })
  );
  vinculatedStore.form.Identikit = identikitWithActuacionPersonaFisicaID;
};

const handleValidationStatus = (errors) => {
  validationErrors.value = { ...validationErrors.value, ...errors };
};

const handleUpdatePerson = (person) => {
  person.PersonaFisicaID =
    selectedPersona?.value?.PersonaFisicaID !== undefined
      ? selectedPersona?.value?.PersonaFisicaID
      : null;
  person.ActuacionPersonaFisicaID =
    selectedPersona?.value?.ActuacionPersonaFisicaID !== undefined
      ? selectedPersona?.value?.ActuacionPersonaFisicaID
      : "";
  vinculatedStore.form = person;
  vinculatedStore.validateForm();
};

const handlePersonValidationStatus = (errores) => {
  vinculatedStore.setValidationErrors(errores);
};

function setIsDisabled(value) {
  identikitRef.value = value;
}
</script>
<style scoped>
.customCard {
  border-radius: 8px;
  border-left: 6px solid #d97706;
  background-color: #eeeeee;
  box-shadow: 0px 4px 4px 0px #00000040;
  max-width: 448px;
  max-height: 104px;
  display: flex;
  justify-content: start;
  align-items: center;
}

.cardTitle {
  font-weight: 700;
  font-style: bold;
  font-size: 20px;
  color: black;
}

.cardText {
  font-weight: 700;
  font-style: bold;
  font-size: 16px;
  color: black;
}

>>> .p-card .p-card-body {
  width: 100% !important;
}

.plusButton {
  background-color: #0b5195;
  border-radius: 6px;
  border: none;
  font-weight: 400;
  font-size: 16px;
}

.plusButton:focus {
  background-color: #0b5195;
  border: none;
}
.plusButton:hover {
  background-color: #0b519580;
  border-radius: 6px;
}

/* .leftPanel {
  flex: 0 0 420px;
  overflow-y: auto;
} */

.leftPanel {
  max-width: 448px;
  flex: 1;
}

.rightPanel {
  flex: 2;
}

.formGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 2rem;
}

.inputContainer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 435px;
}

.labelInput {
  color: #0b5195;
  font-size: 16px;
  font-weight: 400;
}

.input {
  border: 1px solid #0b5195;
  border-radius: 6px;
  width: 100%; /* importante */
  color: #9ca3af;
}

.input:focus {
  border: none !important;
}

.informativeText {
  text-align: center;
  font-size: 28px;
  color: #0b5195;
  font-weight: 500;
}

.plusButton.p-button.p-component {
  display: inline-block !important;
}

/* #app
  > div:nth-child(2)
  > div
  > div.content
  > div:nth-child(2)
  > div
  > div.w-11.bg-white.shadow-2.py-1.px-3
  > div
  > div.w-full.card.p-tabview.p-component
  > div.p-tabview-panels
  > div:nth-child(5)
  > div:nth-child(2)
  > div
  > div
  > div
  > div.ml-5.rightPanel
  > div
  > div:nth-child(1)
  > div
  > div
  > div
  > div.grid.flex
  > div {
  background-color: aqua;
} */
</style>
